import avatars from '$lib/components/common/AvatarLibrary.json';
import { getOptimizedAvatarUrl } from '$lib/components/blog/article/performantImage';
import { avatarBucket } from '$lib/supabase';

// Static caches shared across all component instances
const userRandomAvatarCache = new Map<string, string>();
const avatarUrlCache = new Map<string, string | null>();

/**
 * Check if an avatar URL is the default placeholder
 */
export const isDefaultAvatar = (url: string | null | undefined): boolean => {
	return !url || url === 'avatar-default.svg' || url.includes('avatar-default');
};

/**
 * Check if an avatar URL is a static random avatar from our library
 */
export const isRandomAvatar = (url: string): boolean => {
	return url?.startsWith('/img/avatars/');
};

/**
 * Get a consistent random avatar for a specific user
 * This ensures the same user always gets the same random avatar across the site
 */
export const getUserRandomAvatar = (userId: string | null): string => {
	if (!userId) {
		const anon = getAnonymousRandomAvatar();
		return anon;
	}

	if (userRandomAvatarCache.has(userId)) {
		return userRandomAvatarCache.get(userId)!;
	}

	const randomAvatar = avatars[Math.floor(Math.random() * avatars.length)];
	userRandomAvatarCache.set(userId, randomAvatar);
	return randomAvatar;
};

/**
 * Get a random avatar for anonymous/unknown users
 */
export const getAnonymousRandomAvatar = (): string => {
	return avatars[Math.floor(Math.random() * avatars.length)];
};

/**
 * Get a public avatar URL from Supabase
 */
export const getPublicAvatarUrl = (path: string, size: number = 200): string => {
	if (!path) return '';
	// If it's a static avatar, return as-is
	if (path.startsWith('/img/avatars/') || path.startsWith('img/avatars/')) {
		return path.startsWith('/') ? path : '/' + path;
	}
	// Otherwise, generate Supabase public URL
	const { data } = avatarBucket('avatars').getPublicUrl(path, {
		transform: {
			width: size,
			height: size,
			resize: 'cover',
			quality: 70,
			format: 'origin'
		}
	});
	return data.publicUrl;
};

/**
 * Resolve the final avatar URL for display
 * Handles default avatars, random avatars, and uploaded avatars
 */
export const resolveAvatarUrl = async (
	avatarUrl: string | null | undefined,
	userId?: string | null,
	size: number = 200
): Promise<string> => {
	// If no avatar URL provided, use random avatar
	if (!avatarUrl) {
		return getUserRandomAvatar(userId || null);
	}

	// If it's the default avatar, use random avatar
	if (isDefaultAvatar(avatarUrl)) {
		return getUserRandomAvatar(userId || null);
	}

	// If it's already a random avatar from our library, use it directly
	if (isRandomAvatar(avatarUrl)) {
		return avatarUrl;
	}

	// If it's a public bucket path, return the public URL
	if (
		typeof avatarUrl === 'string' &&
		!avatarUrl.startsWith('http') &&
		!avatarUrl.startsWith('blob:')
	) {
		const url = getPublicAvatarUrl(avatarUrl, size);
		return url;
	}

	// If it's already a public URL, use it directly
	if (avatarUrl.startsWith('http')) {
		return avatarUrl;
	}

	// Fallback to random avatar
	return getUserRandomAvatar(userId || null);
};

/**
 * Get avatar URL for navbar (small size, optimized for performance)
 */
export const getNavbarAvatarUrl = async (
	avatarUrl: string | null | undefined,
	userId?: string | null
): Promise<string> => {
	return resolveAvatarUrl(avatarUrl, userId, 48);
};

/**
 * Get avatar URL for profile pages (larger size)
 */
export const getProfileAvatarUrl = async (
	avatarUrl: string | null | undefined,
	userId?: string | null
): Promise<string> => {
	return resolveAvatarUrl(avatarUrl, userId, 200);
};

/**
 * Get avatar URL for blog/article displays (medium size)
 */
export const getBlogAvatarUrl = async (
	avatarUrl: string | null | undefined,
	userId?: string | null
): Promise<string> => {
	return resolveAvatarUrl(avatarUrl, userId, 100);
};

/**
 * Clear caches (useful for testing or memory management)
 */
export const clearAvatarCaches = (): void => {
	userRandomAvatarCache.clear();
	avatarUrlCache.clear();
};
